// Curated photo registry of "discover Panama" places used by:
//  - Home page "Découvrir le Panama" visual gallery
//  - decouvrir-panama-8-lieux-incontournables pillar article
//  - Future destination guides
//
// Each entry is keyed by canonical place slug (kebab-case).
// Photos are hot-linked Unsplash assets — VERIFIED INDIVIDUALLY by
// downloading + visually inspecting the actual image. Earlier (May 2026)
// some assets were taken from Unsplash searches without manual visual
// confirmation and turned out to depict other cities (London, North-American
// lake cabins, etc.) — all photos here are now verified to depict the
// actual Panamanian location they're attached to.
//
// PROCESS to add a new entry:
//   1. Find candidate on https://unsplash.com (search "panama city",
//      "casco viejo", etc.). Skip plus.unsplash.com (premium, no hotlink).
//   2. Open the photo page, copy the image URL — extract the chunk after
//      `photo-` (e.g. 1524544187526-2b2855b65f16).
//   3. Verify VISUALLY: open https://images.unsplash.com/photo-{asset}
//      and confirm the scene actually depicts the claimed place.
//   4. Add the entry below with `verified: true` + photographer credit.

import { buildUnsplashUrl } from './photos';

const a = (handle) => `https://unsplash.com/${handle}`;

export const PANAMA_PLACES = [
  {
    slug: 'panama-city',
    name: { fr: 'Panama City', en: 'Panama City', es: 'Ciudad de Panamá' },
    region: { fr: 'Capitale, Pacifique', en: 'Capital, Pacific', es: 'Capital, Pacífico' },
    teaser: {
      fr: 'Skyline gratte-ciel + vieille ville UNESCO + Canal du monde, le tout en 30 minutes de voiture.',
      en: 'Skyscraper skyline + UNESCO old town + the world Canal, all within 30 minutes drive.',
      es: 'Skyline de rascacielos + casco antiguo UNESCO + Canal del mundo, todo en 30 minutos.',
    },
    photo: {
      // VERIFIED: aerial view of Cinta Costera + Avenida Balboa towers at sunset.
      // Marina visible bottom-left, lit tennis courts. Real Panama City skyline.
      asset: '1524544187526-2b2855b65f16',
      alt: {
        fr: 'Skyline Panama City : tours d\'Avenida Balboa et marina au coucher de soleil',
        en: 'Panama City skyline: Avenida Balboa towers and marina at sunset',
        es: 'Skyline Ciudad de Panamá: torres de Avenida Balboa y marina al atardecer',
      },
      author: 'Miguel Bruna',
      authorUrl: a('@miguelbruna'),
      verified: true,
    },
    bestSeason: { fr: 'Toute l\'année', en: 'Year-round', es: 'Todo el año' },
    duration: { fr: '2-3 jours', en: '2-3 days', es: '2-3 días' },
  },
  {
    slug: 'casco-viejo',
    name: { fr: 'Casco Viejo (San Felipe)', en: 'Casco Viejo (San Felipe)', es: 'Casco Viejo (San Felipe)' },
    region: { fr: 'Panama City', en: 'Panama City', es: 'Ciudad de Panamá' },
    teaser: {
      fr: 'Quartier colonial classé UNESCO depuis 1997. Plaza de la Independencia, rooftops bordeaux, ambiance fin de journée magique.',
      en: 'UNESCO-listed colonial quarter since 1997. Plaza de la Independencia, bordeaux-tinted rooftops, magic golden hour.',
      es: 'Barrio colonial declarado UNESCO desde 1997. Plaza de la Independencia, azoteas color burdeos, atmósfera mágica al atardecer.',
    },
    photo: {
      // VERIFIED: pastel-colored colonial facades with wrought-iron balconies,
      // ground-floor archways, palm trees. Quintessential Casco Viejo.
      asset: '1656781313303-51379444b553',
      alt: {
        fr: 'Façades coloniales pastel avec balcons en fer forgé, Casco Viejo',
        en: 'Pastel colonial facades with wrought-iron balconies, Casco Viejo',
        es: 'Fachadas coloniales pastel con balcones de hierro forjado, Casco Viejo',
      },
      author: 'Kaori Hirakata',
      authorUrl: a('@kaorihirakata'),
      verified: true,
    },
    bestSeason: { fr: 'Toute l\'année', en: 'Year-round', es: 'Todo el año' },
    duration: { fr: '½-1 jour', en: '½-1 day', es: '½-1 día' },
  },
  {
    slug: 'san-blas',
    name: { fr: 'Îles San Blas (Guna Yala)', en: 'San Blas Islands (Guna Yala)', es: 'Islas San Blas (Guna Yala)' },
    region: { fr: 'Caraïbes panaméennes', en: 'Panamanian Caribbean', es: 'Caribe panameño' },
    teaser: {
      fr: '378 îles, autonomie indigène Guna, sable blanc et eaux turquoise. Pas de Wi-Fi, pas de chaînes hôtelières — l\'inverse du tourisme de masse.',
      en: '378 islands, Guna indigenous autonomy, white sand and turquoise water. No Wi-Fi, no hotel chains — the opposite of mass tourism.',
      es: '378 islas, autonomía indígena Guna, arena blanca y aguas turquesas. Sin Wi-Fi, sin cadenas hoteleras — lo opuesto al turismo masivo.',
    },
    photo: {
      // VERIFIED: aerial drone shot of San Blas archipelago — palm-covered
      // islands, sailboats anchored in turquoise reef shallows.
      // Watermarked "© Maximilian Herrmann, SY Nikaia" (visible bottom-right).
      asset: '1705094638863-be5337957ac3',
      alt: {
        fr: 'Vue aérienne de l\'archipel San Blas — îles palmiers, voiliers, récif turquoise',
        en: 'Aerial view of the San Blas archipelago — palm-covered islands, sailboats, turquoise reef',
        es: 'Vista aérea del archipiélago San Blas — islas con palmeras, veleros, arrecife turquesa',
      },
      author: 'Maximilian Herrmann',
      authorUrl: a('@maxherr'),
      verified: true,
    },
    bestSeason: { fr: 'Décembre – avril (mer calme)', en: 'December – April (calm sea)', es: 'Diciembre – abril (mar calmo)' },
    duration: { fr: '2-4 jours', en: '2-4 days', es: '2-4 días' },
  },
  {
    slug: 'boquete',
    name: { fr: 'Boquete & Highlands de Chiriquí', en: 'Boquete & Chiriquí Highlands', es: 'Boquete y Tierras Altas de Chiriquí' },
    region: { fr: 'Montagnes de l\'Ouest', en: 'Western Highlands', es: 'Tierras Altas del Oeste' },
    teaser: {
      fr: 'À 1 200 m d\'altitude. Café Geisha primé mondial, Volcán Barú (les 2 océans visibles depuis le sommet), colibris, douceur.',
      en: 'At 1,200 m. World-class Geisha coffee, Volcán Barú (both oceans visible from the summit), hummingbirds, mild climate.',
      es: 'A 1,200 m. Café Geisha de clase mundial, Volcán Barú (ambos océanos visibles desde la cumbre), colibríes, clima suave.',
    },
    photo: {
      // VERIFIED: Boquete valley overview — Caldera river winding through green
      // Chiriquí highlands, the village in the valley, mountain ridges of the
      // Cordillera Central in the background.
      asset: '1604401442263-e570cbcc68cf',
      alt: {
        fr: 'Vallée de Boquete avec rivière Caldera et montagnes de Chiriquí',
        en: 'Boquete valley with Caldera river and Chiriquí mountains',
        es: 'Valle de Boquete con río Caldera y montañas de Chiriquí',
      },
      author: 'Betzy Arosemena',
      authorUrl: a('@betzyarosemena'),
      verified: true,
    },
    bestSeason: { fr: 'Décembre – avril', en: 'December – April', es: 'Diciembre – abril' },
    duration: { fr: '3-5 jours', en: '3-5 days', es: '3-5 días' },
  },
  {
    slug: 'bocas-del-toro',
    name: { fr: 'Bocas del Toro', en: 'Bocas del Toro', es: 'Bocas del Toro' },
    region: { fr: 'Caraïbes Nord-Ouest', en: 'NW Caribbean', es: 'Caribe noroeste' },
    teaser: {
      fr: 'Archipel caribéen, surfeurs, plongeurs, maisons sur pilotis colorées. Tortues luth juillet-octobre.',
      en: 'Caribbean archipelago, surfers, divers, colorful stilt houses. Leatherback turtles July-October.',
      es: 'Archipiélago caribeño, surfistas, buzos, palafitos coloridos. Tortugas laúd julio-octubre.',
    },
    photo: {
      // VERIFIED: thatched-roof palafito (stilt cottage) over crystal-clear
      // shallow Caribbean water, mangroves on shore — classic Bocas del Toro.
      asset: '1443803098578-cb3824137a44',
      alt: {
        fr: 'Palafito au toit de chaume sur eau caribéenne cristalline, Bocas del Toro',
        en: 'Thatched-roof stilt cottage over crystal Caribbean water, Bocas del Toro',
        es: 'Palafito con techo de paja sobre agua caribeña cristalina, Bocas del Toro',
      },
      author: 'Pablo García Saldaña',
      authorUrl: a('@pablogarciasaldana'),
      verified: true,
    },
    bestSeason: { fr: 'Septembre – octobre, mars – avril', en: 'September – October, March – April', es: 'Septiembre – octubre, marzo – abril' },
    duration: { fr: '3-5 jours', en: '3-5 days', es: '3-5 días' },
  },
  // INTENTIONALLY HIDDEN until photos are visually verified to depict the
  // actual place (and not a generic tropical photo from elsewhere):
  //
  //  - El Valle de Antón (volcanic caldera) — needs a verified photo of
  //    the actual caldera or village, NOT a generic rainforest.
  //  - Gamboa & Lac Gatún — needs a verified photo of Pipeline Road,
  //    Gatún Lake from a boat, OR Monkey Island — NOT a generic canal cargo.
  //  - Pedasí & Azuero — needs a verified photo of a Pacific Pedasí beach,
  //    NOT a Maldives-style stock photo.
  //
  // Reactivate each entry below by replacing `asset` with a verified one
  // (download the image, confirm visually it shows the actual place).
];

/**
 * Returns a place by slug, or null.
 */
export function getPlace(slug) {
  return PANAMA_PLACES.find((p) => p.slug === slug) ?? null;
}

/**
 * Returns N places (deterministic per day-of-year so the home gallery
 * is stable for everyone in the same day, refreshing daily). If N exceeds
 * the available verified places, we cap at PANAMA_PLACES.length.
 */
export function getDailyPlaces(n = 6) {
  const day = Math.floor(Date.now() / 86_400_000);
  const out = [...PANAMA_PLACES];
  for (let i = out.length - 1; i > 0; i--) {
    const j = (day + i * 31) % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.slice(0, Math.min(n, PANAMA_PLACES.length));
}

export { buildUnsplashUrl };
